package org.teddapp.project.sync

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlin.concurrent.Volatile
import kotlinx.coroutines.flow.merge
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.withContext
import org.teddapp.project.ui.AppSettings

/**
 * Coordonne le moteur de sync avec l'UI et la persistance des préférences.
 *
 *  - Compte les changements en attente (`selectDirty` agrégé sur toutes les entités),
 *    à chaque écriture locale : rien n'est relu quand rien ne bouge.
 *  - Expose un `StateFlow<SyncUiState>` consommé par Settings.
 *  - `setEnabled` persiste le flag et déclenche une sync immédiate si activé.
 *  - `syncNow` lance un cycle push+pull. Concurrent click protégé.
 *
 * À instancier au démarrage de l'app (cf. main.kt desktop) avec un `CoroutineScope`
 * lié au cycle de vie de l'app.
 */
@OptIn(kotlinx.coroutines.FlowPreview::class)
class SyncManager(
    private val db: TeddDatabase,
    private val engine: SyncEngine,
    private val appSettings: AppSettings,
    private val scope: CoroutineScope,
    /**
     * Cycle du journal du noyau, exécuté avant celui des tables. Le noyau tient son
     * propre curseur : un échec réseau ici n'empêche pas le reste de se synchroniser.
     */
    private val journalCycle: (suspend () -> Unit)? = null,
    /** Cadence du battement court, réglable pour les bancs de mesure. */
    private val liveTickMs: Long = LIVE_TICK_MS,
) {

    private val _state = MutableStateFlow(initialState())
    val state: StateFlow<SyncUiState> = _state.asStateFlow()

    /** Préférences locales, non synchronisées : le dernier passage et la dernière erreur par table. */
    private val prefs = org.teddapp.project.db.repo.PreferencesRepository(db)
    private val lastOk = mutableMapOf<EntityKind, Long>()
    private val lastError = mutableMapOf<EntityKind, String>()

    /** Échecs d'affilée et instant de la prochaine tentative automatique. */
    private var failuresInARow = 0
    private var nextRetryAt = 0L
    private val historyJson = kotlinx.serialization.json.Json { ignoreUnknownKeys = true }

    init {
        scope.launch { loadTableMemory(); refreshTables() }
        scope.launch { autoBackupLoop() }
        scope.launch { liveLoop() }
        scope.launch { timerPushLoop() }
        scope.launch { dirtyPushLoop() }
        // Si la sync est activée au boot et qu'on a déjà synchronisé une fois,
        // on tente immédiatement (rattrapage).
        if (appSettings.syncEnabled && appSettings.syncLastAt > 0L) {
            syncNow(trigger = SyncTrigger.BootCatchup)
        }
    }

    fun setEnabled(on: Boolean) {
        appSettings.applySyncEnabled(on)
        _state.update { it.copy(enabled = on) }
        if (on) syncNow(trigger = SyncTrigger.Manual)
    }

    fun syncNow(trigger: SyncTrigger = SyncTrigger.Manual): Job {
        if (_state.value.status == SyncStatus.Syncing) {
            return Job().apply { complete() }
        }
        return scope.launch {
            val startedAt = org.teddapp.project.db.nowMillis()
            val pendingBefore = countPending()
            _state.update { it.copy(status = SyncStatus.Syncing, errorMessage = null) }
            journalCycle?.let { cycle -> runCatching { cycle() } }
            var lastResult: SyncResult? = null
            val outcome: Triple<Boolean, String?, Long> = try {
                val result = withContext(Dispatchers.Default) { engine.sync() }
                lastResult = result
                when (result) {
                    is SyncResult.Success -> Triple(true, null, org.teddapp.project.db.nowMillis())
                    is SyncResult.Failure -> Triple(false, result.summary ?: result.cause.message ?: "Échec sur ${result.entity?.tableName ?: "?"}", org.teddapp.project.db.nowMillis())
                }
            } catch (e: Throwable) {
                Triple(false, e.message ?: e::class.simpleName ?: "Erreur inconnue", org.teddapp.project.db.nowMillis())
            }
            val (success, err, finishedAt) = outcome
            // Lisible dans le journal de l'appareil, là où il n'y a pas d'écran pour le dire (montre).
            println(if (success) "[sync] passage réussi" else "[sync] passage en échec : $err")
            rememberTableOutcome(outcomeResult = lastResult, at = finishedAt)
            // Ce qui a été envoyé l'est pour de bon, même si une table a échoué ensuite :
            // le moteur marque chaque lot accepté. On compte ce qui n'attend plus.
            val pushed = (pendingBefore - countPending()).coerceAtLeast(0)
            val entry = SyncRunEntry(
                id = "run_$startedAt",
                startedAt = startedAt,
                finishedAt = finishedAt,
                success = success,
                trigger = trigger,
                pushedCount = if (success) pendingBefore else pushed,
                errorMessage = err,
            )
            // La dernière synchro, c'est le dernier moment où des données sont parties.
            if (success || pushed > 0) appSettings.applySyncLastAt(finishedAt)
            // Un serveur en panne n'a pas besoin qu'on insiste toutes les quinze secondes :
            // l'attente double à chaque échec, jusqu'à une demi-heure, et retombe au premier succès.
            if (success) {
                failuresInARow = 0
                nextRetryAt = 0L
            } else {
                failuresInARow += 1
                nextRetryAt = finishedAt + backoffDelayMs(failuresInARow)
            }
            val history = (listOf(entry) + _state.value.history).take(HISTORY_LIMIT)
            runCatching { prefs.set(KEY_HISTORY, historyJson.encodeToString(kotlinx.serialization.builtins.ListSerializer(SyncRunEntry.serializer()), history)) }
            _state.update {
                it.copy(
                    status = if (success) SyncStatus.Idle else SyncStatus.Error,
                    lastSyncAt = if (success || pushed > 0) finishedAt else it.lastSyncAt,
                    pendingChanges = countPending(),
                    errorMessage = err,
                    history = history,
                    nextRetryAt = nextRetryAt.takeIf { at -> at > 0L },
                    consecutiveFailures = failuresInARow,
                )
            }
            refreshTables()
        }
    }

    // ── Tableau de bord par table ──────────────────────────────────────

    private suspend fun loadTableMemory() {
        val all = prefs.loadAll()
        for (entity in EntityKind.entries) {
            all["sync_ok_${entity.tableName}"]?.toLongOrNull()?.let { lastOk[entity] = it }
            all["sync_err_${entity.tableName}"]?.let { lastError[entity] = it }
        }
        val history = all[KEY_HISTORY]?.let { raw ->
            runCatching { historyJson.decodeFromString(kotlinx.serialization.builtins.ListSerializer(SyncRunEntry.serializer()), raw) }.getOrNull()
        }.orEmpty()
        if (history.isNotEmpty()) _state.update { it.copy(history = history) }
    }

    /** Vrai tant qu'un échec récent demande de patienter : les cycles automatiques s'abstiennent. */
    private fun holdingBack(): Boolean = org.teddapp.project.db.nowMillis() < nextRetryAt

    /**
     * Après un cycle complet : chaque table qui n'est pas dans les échecs est passée.
     * On l'écrit en préférence, pour que le tableau de bord survive au redémarrage.
     */
    private suspend fun rememberTableOutcome(outcomeResult: SyncResult?, at: Long) {
        val failed: Map<EntityKind, String> = when (outcomeResult) {
            is SyncResult.Failure -> outcomeResult.failures.associate { it.entity to it.message }
                .ifEmpty { outcomeResult.entity?.let { mapOf(it to (outcomeResult.cause.message ?: "erreur")) }.orEmpty() }
            SyncResult.Success -> emptyMap()
            null -> return // exception hors moteur : on ne sait rien de plus par table
        }
        for (entity in EntityKind.entries) {
            val err = failed[entity]
            if (err == null) {
                lastOk[entity] = at
                lastError.remove(entity)
                prefs.set("sync_ok_${entity.tableName}", at.toString())
                prefs.delete("sync_err_${entity.tableName}")
            } else {
                lastError[entity] = err
                prefs.set("sync_err_${entity.tableName}", err)
            }
        }
    }

    /** Recompte le local, garde les comptes distants déjà connus. */
    fun refreshTables() {
        scope.launch {
            val known = _state.value.tables.associate { it.entity to it.remoteCount }
            val rows = withContext(Dispatchers.Default) {
                EntityKind.entries.map { entity ->
                    SyncTableStatus(
                        entity = entity,
                        pending = SyncEntityOps.selectDirty(entity, db).size,
                        total = SyncEntityOps.selectAllForExport(entity, db).size,
                        lastOkAt = lastOk[entity],
                        lastError = lastError[entity],
                        remoteCount = known[entity],
                    )
                }
            }
            _state.update { it.copy(tables = rows) }
        }
    }

    /** Demande au serveur le compte de chaque table et le met en regard du local. */
    fun checkRemote() {
        if (_state.value.checkingRemote) return
        scope.launch {
            _state.update { it.copy(checkingRemote = true) }
            val counts = mutableMapOf<EntityKind, Long?>()
            for (entity in EntityKind.entries) {
                counts[entity] = runCatching { withContext(Dispatchers.Default) { engine.remoteCount(entity) } }.getOrNull()
            }
            val at = org.teddapp.project.db.nowMillis()
            _state.update { st ->
                st.copy(
                    checkingRemote = false,
                    remoteCheckedAt = at,
                    tables = st.tables.map { t -> t.copy(remoteCount = counts[t.entity]) },
                )
            }
        }
    }

    /**
     * Battement court : le chronomètre partagé et le journal du noyau circulent en
     * quelques secondes, sans payer les vingt-six tables à chaque tour.
     *
     * C'est ce qui sépare la synchronisation de la sauvegarde. La sauvegarde peut
     * attendre la nuit ; une course lancée sur le téléphone doit apparaître sur
     * l'ordinateur pendant qu'elle tourne, sinon elle ne sert à rien.
     *
     * Les échecs restent silencieux : le cycle complet, lui, les rapporte. Un réseau
     * absent ne doit pas remplir l'écran d'erreurs toutes les cinq secondes.
     */
    private suspend fun liveLoop() {
        while (scope.coroutineContext.isActive) {
            delay(liveTickMs)
            liveCycle()
        }
    }

    /**
     * Le chronomètre part dès qu'on y touche : lancer, mettre en pause ou arrêter une
     * course ici l'envoie tout de suite, sans attendre le prochain battement. Les
     * autres appareils la voient à leur battement suivant.
     */
    private suspend fun timerPushLoop() {
        db.timerStateQueries.selectDirty().asFlow().mapToList(Dispatchers.Default).collect { dirty ->
            if (dirty.isNotEmpty() && canRunQuietly()) liveSync()
        }
    }

    /** Vrai quand un cycle silencieux peut partir : synchro activée, pas de cycle en cours, pas d'attente après échec. */
    private fun canRunQuietly(): Boolean =
        appSettings.syncEnabled && _state.value.status != SyncStatus.Syncing && !holdingBack()

    /** Le journal du noyau, puis les tables qui doivent circuler vite. */
    private suspend fun liveSync() {
        journalCycle?.let { cycle -> runCatching { cycle() } }
        runCatching { withContext(Dispatchers.Default) { engine.syncOnly(LIVE_ENTITIES) } }
    }

    /**
     * Un tour du battement. Sans sonnette, c'est lui qui fait circuler le chronomètre
     * et le journal : un passage à chaque battement. Sonnette branchée, l'envoi suit
     * l'écriture ([dirtyPushLoop]) et le tirage suit la sonnette ([remoteChanged]) :
     * le battement ne fait plus qu'un passage de sûreté tous les six tours, au cas où
     * un coup de sonnette se serait perdu. Au repos, rien ne part et rien n'est relu.
     */
    suspend fun liveCycle() {
        if (!canRunQuietly()) return
        liveTicks += 1
        if (bellLive && liveTicks % 6 != 0) return
        liveSync()
    }
    private var liveTicks = 0

    /** Vrai quand la sonnette du serveur est branchée ; posé par celui qui la tient. */
    @Volatile var bellLive: Boolean = false

    /**
     * Un autre appareil a écrit : on tire tout de suite. Plusieurs coups de sonnette
     * rapprochés ne font qu'un tirage.
     */
    private var bellPull: Job? = null
    fun remoteChanged() {
        if (bellPull?.isActive == true) return
        bellPull = scope.launch {
            delay(BELL_DEBOUNCE_MS)
            if (!appSettings.syncEnabled || _state.value.status == SyncStatus.Syncing) return@launch
            // Le journal aussi : les heures et les paliers d'un autre appareil viennent par lui.
            journalCycle?.let { cycle -> runCatching { cycle() } }
            runCatching { withContext(Dispatchers.Default) { engine.pullOnly() } }
            _state.update { it.copy(pendingChanges = countPending()) }
        }
    }

    /**
     * Envoi dès l'écriture : chaque table à envoyer est observée ; une écriture locale
     * déclenche l'envoi un tiers de seconde plus tard, le temps qu'un geste groupé
     * (une tâche et son statut) arrive en entier. L'autre appareil, prévenu par la
     * sonnette, l'a sous la seconde.
     *
     * C'est aussi là que le compteur du badge se met à jour : il ne bouge qu'à
     * l'écriture, personne ne le recompte quand rien ne change.
     */
    private suspend fun dirtyPushLoop() {
        val signals = EntityKind.entries.map { SyncEntityOps.dirtyQuery(it, db).asFlow().map { Unit } }
        merge(*signals.toTypedArray())
            .debounce(DIRTY_DEBOUNCE_MS)
            .collect {
                val pending = withContext(Dispatchers.Default) { countPending() }
                _state.update { if (it.pendingChanges == pending) it else it.copy(pendingChanges = pending) }
                if (pending == 0 || !canRunQuietly()) return@collect
                runCatching { withContext(Dispatchers.Default) { engine.pushOnly() } }
                _state.update { it.copy(pendingChanges = countPending()) }
            }
    }

    /**
     * Boucle d'auto-backup : tick chaque minute, déclenche syncNow() quand
     * la fréquence configurée est dépassée depuis le dernier lastSyncAt.
     */
    private suspend fun autoBackupLoop() {
        while (scope.coroutineContext.isActive) {
            delay(AUTO_TICK_MS)
            if (!appSettings.syncEnabled || !appSettings.backupAuto) continue
            if (_state.value.status == SyncStatus.Syncing) continue
            if (holdingBack()) continue
            val intervalMs = frequencyToIntervalMs(appSettings.backupFrequency)
            val last = appSettings.syncLastAt
            if (last == 0L) {
                // Jamais sync : on déclenche un premier cycle.
                syncNow(trigger = SyncTrigger.Auto)
                continue
            }
            if (org.teddapp.project.db.nowMillis() - last >= intervalMs) {
                syncNow(trigger = SyncTrigger.Auto)
            }
        }
    }

    private fun frequencyToIntervalMs(key: String): Long = when (key) {
        "daily" -> 24L * 3_600_000L
        "weekly" -> 7L * 24L * 3_600_000L
        "twice_monthly" -> 15L * 24L * 3_600_000L
        "monthly" -> 30L * 24L * 3_600_000L
        else -> 15L * 24L * 3_600_000L
    }

    private fun countPending(): Int {
        var total = 0
        for (entity in EntityKind.entries) {
            total += SyncEntityOps.selectDirty(entity, db).size
        }
        return total
    }

    private fun initialState(): SyncUiState = SyncUiState(
        enabled = appSettings.syncEnabled,
        status = SyncStatus.Idle,
        lastSyncAt = appSettings.syncLastAt.takeIf { it > 0L },
        pendingChanges = countPending(),
        errorMessage = null,
    )

    companion object {
        private const val KEY_HISTORY = "sync_history"

        /** Attente avant la prochaine tentative automatique : 1, 2, 4, 8, 16 minutes, puis 30 au plus. */
        fun backoffDelayMs(failuresInARow: Int): Long {
            val minutes = 1L shl (failuresInARow - 1).coerceIn(0, 5)
            return minutes.coerceAtMost(30L) * 60_000L
        }

        private const val AUTO_TICK_MS = 60_000L  // 1 min — fréquence du check du scheduler
        private const val LIVE_TICK_MS = 5_000L  // 5 s — battement du chronomètre partagé
        private const val DIRTY_DEBOUNCE_MS = 300L  // écriture locale → envoi
        private const val BELL_DEBOUNCE_MS = 100L   // sonnette → tirage

        /**
         * Ce qui doit circuler vite : le chronomètre, et la liste du jour qu'il sert — une
         * tâche cochée au poignet doit l'être sur le téléphone quand on le reprend en main.
         * Tout le reste attend le cycle complet.
         */
        private val LIVE_ENTITIES = listOf(EntityKind.TimerState, EntityKind.PomodoroSession, EntityKind.PomodoroItem)
        private const val HISTORY_LIMIT = 50
    }
}
